import { parseCsvTable, parseUrlList } from "./csv";
import { isValidJobUrl } from "./resolve-job-url";

export type JobCsvField =
  | "url"
  | "company"
  | "role"
  | "location"
  | "compensation"
  | "notes"
  | "jobDescription"
  | "ignore";

export const JOB_CSV_FIELDS: { value: JobCsvField; label: string }[] = [
  { value: "url", label: "Job URL" },
  { value: "company", label: "Company" },
  { value: "role", label: "Job title / role" },
  { value: "location", label: "Location" },
  { value: "compensation", label: "Compensation" },
  { value: "notes", label: "Notes" },
  { value: "jobDescription", label: "Job description" },
  { value: "ignore", label: "Ignore" },
];

const ALIASES: Record<Exclude<JobCsvField, "ignore">, string[]> = {
  url: ["url", "link", "job_url", "joburl", "job link", "posting", "href"],
  company: ["company", "employer", "organization", "org", "company_name", "companyname"],
  role: ["role", "title", "position", "job_title", "jobtitle", "job"],
  location: ["location", "city", "geo", "place", "office"],
  compensation: ["compensation", "salary", "pay", "pay_range", "comp"],
  notes: ["notes", "note", "comment", "comments"],
  jobDescription: [
    "jobdescription",
    "job_description",
    "description",
    "jd",
    "desc",
  ],
};

export type ColumnMapping = Record<number, JobCsvField>;

export interface MappedJobRow {
  id: string;
  url: string;
  company: string;
  role: string;
  location?: string;
  compensation?: string;
  notes?: string;
  jobDescription?: string;
  selected: boolean;
  enrichStatus?: "idle" | "pending" | "ok" | "manual" | "failed";
  tier?: string;
  provider?: string;
  hint?: string;
  resolvedUrl?: string;
}

/** Lightweight URL normalize for client-side dedupe (strip hash + common tracking). */
export function normalizeJobUrlClient(url: string): string {
  try {
    const u = new URL(url.trim());
    u.hash = "";
    const drop = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "fbclid",
      "gclid",
      "refId",
      "refid",
      "trackingId",
      "eBP",
      "alternateChannel",
      "from",
      "trk",
    ];
    for (const key of drop) u.searchParams.delete(key);
    if (u.hostname.includes("linkedin.")) {
      const m = u.pathname.match(/\/jobs\/view\/(\d+)/);
      if (m) {
        u.pathname = `/jobs/view/${m[1]}`;
        u.search = "";
      }
    }
    if (u.pathname.length > 1 && u.pathname.endsWith("/")) {
      u.pathname = u.pathname.slice(0, -1);
    }
    return u.toString();
  } catch {
    return url.trim();
  }
}

function normHeader(h: string): string {
  return h.toLowerCase().replace(/[_\s-]+/g, " ").trim();
}

/** Auto-map CSV headers to job fields. */
export function autoMapHeaders(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const used = new Set<JobCsvField>();

  headers.forEach((header, index) => {
    const n = normHeader(header);
    if (/^col_\d+$/.test(header) && index === 0) {
      mapping[index] = "url";
      used.add("url");
      return;
    }
    let matched: JobCsvField | undefined;
    for (const [field, aliases] of Object.entries(ALIASES) as [
      Exclude<JobCsvField, "ignore">,
      string[],
    ][]) {
      if (used.has(field)) continue;
      if (aliases.some((a) => n === a || n.replace(/\s/g, "") === a.replace(/\s/g, ""))) {
        matched = field;
        break;
      }
    }
    mapping[index] = matched ?? "ignore";
    if (matched) used.add(matched);
  });

  // If no url mapped but first column looks unused as company, prefer url on col 0
  if (![...Object.values(mapping)].includes("url") && headers.length > 0) {
    mapping[0] = "url";
  }

  return mapping;
}

export function mappingStorageKey(headers: string[]): string {
  return `career-ops-job-csv-map:${headers.map(normHeader).join("|")}`;
}

export function loadSavedMapping(headers: string[]): ColumnMapping | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(mappingStorageKey(headers));
    if (!raw) return null;
    return JSON.parse(raw) as ColumnMapping;
  } catch {
    return null;
  }
}

export function saveMapping(headers: string[], mapping: ColumnMapping): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(mappingStorageKey(headers), JSON.stringify(mapping));
  } catch {
    // ignore quota
  }
}

function cell(row: string[], index: number | undefined): string {
  if (index == null || index < 0) return "";
  return (row[index] ?? "").trim();
}

/** Apply column mapping to CSV rows; dedupe by normalized URL. */
export function applyJobMapping(
  headers: string[],
  rows: string[][],
  mapping: ColumnMapping,
): MappedJobRow[] {
  const indexOf = (field: JobCsvField): number | undefined => {
    const entry = Object.entries(mapping).find(([, f]) => f === field);
    return entry ? Number(entry[0]) : undefined;
  };

  const urlIdx = indexOf("url");
  const companyIdx = indexOf("company");
  const roleIdx = indexOf("role");
  const locationIdx = indexOf("location");
  const compensationIdx = indexOf("compensation");
  const notesIdx = indexOf("notes");
  const descIdx = indexOf("jobDescription");

  const seen = new Set<string>();
  const out: MappedJobRow[] = [];

  rows.forEach((row, i) => {
    const rawUrl = cell(row, urlIdx);
    if (!rawUrl || !isValidJobUrl(rawUrl)) return;
    const url = normalizeJobUrlClient(rawUrl);
    if (seen.has(url)) return;
    seen.add(url);

    const company = cell(row, companyIdx);
    const role = cell(row, roleIdx);
    const ready = Boolean(company && role);
    out.push({
      id: `csv-${i}-${url.slice(-24)}`,
      url,
      company,
      role,
      location: cell(row, locationIdx) || undefined,
      compensation: cell(row, compensationIdx) || undefined,
      notes: cell(row, notesIdx) || undefined,
      jobDescription: cell(row, descIdx) || undefined,
      selected: ready,
      enrichStatus: "idle",
    });
  });

  return out;
}

export interface DetectedJobCsv {
  headers: string[];
  rows: string[][];
  hasHeader: boolean;
  mapping: ColumnMapping;
  /** True when input was newline URLs without CSV structure. */
  urlOnly: boolean;
}

/** Detect CSV table or URL list and propose a mapping. */
export function detectJobCsvInput(text: string): DetectedJobCsv {
  const trimmed = text.trim();
  if (!trimmed) {
    return {
      headers: [],
      rows: [],
      hasHeader: false,
      mapping: {},
      urlOnly: true,
    };
  }

  // Prefer URL-list when every non-empty line is a URL (no commas)
  const lines = trimmed.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const allUrls =
    lines.length > 0 &&
    lines.every((l) => !l.includes(",") && (l.startsWith("#") || isValidJobUrl(l)));

  if (allUrls) {
    const urls = parseUrlList(trimmed);
    return {
      headers: ["url"],
      rows: urls.map((u) => [u]),
      hasHeader: false,
      mapping: { 0: "url" },
      urlOnly: true,
    };
  }

  const table = parseCsvTable(trimmed);
  const saved = table.hasHeader ? loadSavedMapping(table.headers) : null;
  const mapping = saved ?? autoMapHeaders(table.headers);
  return {
    headers: table.headers,
    rows: table.rows,
    hasHeader: table.hasHeader,
    mapping,
    urlOnly: false,
  };
}

export function rowImportReady(row: MappedJobRow): boolean {
  return isValidJobUrl(row.url) && Boolean(row.company.trim()) && Boolean(row.role.trim());
}

/** Canonical header row matching Job CSV import schema (auto-map aliases). */
export const JOB_CSV_SAMPLE_HEADERS = [
  "url",
  "company",
  "role",
  "location",
  "compensation",
  "notes",
  "description",
] as const;

/**
 * Sample CSV body matching batch import columns.
 * `url` is required; other fields optional (server enrich fills gaps when possible).
 */
export const JOB_CSV_SAMPLE_CONTENT = [
  JOB_CSV_SAMPLE_HEADERS.join(","),
  [
    "https://boards.greenhouse.io/example/jobs/1234567",
    "Acme Corp",
    "Staff Software Engineer",
    "Remote - US",
    "$180k–$220k",
    "Referred by Alex",
    "Build platform services for growth teams.",
  ]
    .map((c) => (/,|"|\n/.test(c) ? `"${c.replace(/"/g, '""')}"` : c))
    .join(","),
  [
    "https://jobs.lever.co/example/abcdef12-3456-7890-abcd-ef1234567890",
    "Beta Labs",
    "Product Manager",
    "New York, NY",
    "",
    "",
    "",
  ].join(","),
  [
    "https://www.linkedin.com/jobs/view/1234567890",
    "",
    "",
    "",
    "",
    "URL-only row — enrich will try to fill company/title",
    "",
  ]
    .map((c) => (/,|"|\n/.test(c) ? `"${c.replace(/"/g, '""')}"` : c))
    .join(","),
].join("\n") + "\n";

/** Trigger a browser download of the canonical sample job CSV. */
export function downloadJobCsvSample(
  fileName = "career-ops-job-import-sample.csv",
): void {
  const blob = new Blob([JOB_CSV_SAMPLE_CONTENT], {
    type: "text/csv;charset=utf-8",
  });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = fileName;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}
